/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, defineProperty as _defineProperty } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Modal, SkeletonText } from '@carbon/react';
import TagAreaComponent from '../../ResourceTagArea/components/TagArea.js';
import { useTranslation } from '../../../../Utilities/i18n/i18n.js';
import { tagRequests } from '../../../../Utilities/tagging/tagging.js';
import TagAreaSkeleton from '../../ResourceTagArea/ResourceTagArea.Skeleton.js';

// Utilities

var TagModal = function TagModal(_ref) {
  var open = _ref.open,
    onClose = _ref.onClose,
    mock = _ref.mock,
    labelColon = _ref.labelColon,
    isLoading = _ref.isLoading,
    onSave = _ref.onSave,
    resource = _ref.resource,
    error = _ref.error,
    tags = _ref.tags,
    accessTags = _ref.accessTags,
    accessTagPermissions = _ref.accessTagPermissions,
    userTagPermissions = _ref.userTagPermissions,
    tagAreaTooltipDirection = _ref.tagAreaTooltipDirection;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState({
      resourceTags: {
        toAttach: [],
        toDetach: []
      },
      accessTags: {
        toAttach: [],
        toDetach: []
      }
    }),
    _useState2 = _slicedToArray(_useState, 2),
    modifiedTags = _useState2[0],
    setModifiedTags = _useState2[1]; // store tags as they are changed to submit
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isSaving = _useState4[0],
    setIsSaving = _useState4[1];
  var _useState5 = useState(undefined),
    _useState6 = _slicedToArray(_useState5, 2),
    validationError = _useState6[0],
    setValidationError = _useState6[1];
  var handleOnClose = function handleOnClose() {
    onClose();
  };
  var handleTagAreaMessage = function handleTagAreaMessage(err) {
    setValidationError(err);
  };
  var handleOnSave = function handleOnSave() {
    var _modifiedTags$resourc = modifiedTags.resourceTags,
      resourceAttach = _modifiedTags$resourc.toAttach,
      resourceDetach = _modifiedTags$resourc.toDetach,
      _modifiedTags$accessT = modifiedTags.accessTags,
      accessAttach = _modifiedTags$accessT.toAttach,
      accessDetach = _modifiedTags$accessT.toDetach;
    var toAttach = resourceAttach.concat(accessAttach.map(function (tag) {
      return _objectSpread2(_objectSpread2({}, tag), {}, {
        tag_type: 'access'
      });
    }));
    var toDetach = resourceDetach.concat(accessDetach.map(function (tag) {
      return _objectSpread2(_objectSpread2({}, tag), {}, {
        tag_type: 'access'
      });
    }));
    var resourceId = resource.resourceId,
      _resource$type = resource.type,
      type = _resource$type === void 0 ? '' : _resource$type;
    setIsSaving(true);
    if (mock) {
      setTimeout(function () {
        setIsSaving(false);
      }, 500);
      return onSave({
        attached: toAttach.map(function (tag) {
          return {
            tag: tag
          };
        }),
        detached: toDetach.map(function (tag) {
          return {
            tag: tag
          };
        }),
        success: true,
        errors: null
      });
    }
    return tagRequests({
      toAttach: toAttach,
      toDetach: toDetach,
      resource: {
        id: resourceId,
        type: type
      }
    }).then(function (_ref2) {
      var errors = _ref2.errors,
        detached = _ref2.detached,
        attached = _ref2.attached;
      setIsSaving(false);
      onSave({
        attached: attached,
        detached: detached,
        success: errors.length === 0,
        errors: errors
      });
    });
  };
  var handleOnChange = function handleOnChange(tagsObj, tagType) {
    var toAttach = tagsObj.toAttach,
      toDetach = tagsObj.toDetach;
    var newTagList = [];
    var alreadyAttached = tagType === 'resource' ? resource.tags : resource.access_tags;

    // retuned by dialog to attach, some might be attached already, check
    toAttach.forEach(function (tag) {
      // filter out if tag is already attached ex: if a user 'detaches' then 'attached' within the modal without clicking Save yet.
      if (alreadyAttached.find(function (tagName) {
        return tagName === tag.name;
      }) === undefined) {
        newTagList.push(tag);
      }
    });
    var changedTags = {
      toAttach: newTagList,
      toDetach: toDetach
    };
    var type = tagType === 'resource' ? 'resourceTags' : 'accessTags';
    setModifiedTags(_objectSpread2(_objectSpread2({}, modifiedTags), {}, _defineProperty({}, type, changedTags)));
  };
  var handleUserTagOnChange = function handleUserTagOnChange(tagsObj) {
    handleOnChange(tagsObj, 'resource');
  };
  var handleAccessTagOnChange = function handleAccessTagOnChange(tagsObj) {
    handleOnChange(tagsObj, 'access');
  };
  var getModalHeading = function getModalHeading(hasAttachedTags) {
    if (isLoading) {
      return '';
    }
    return hasAttachedTags ? t('Edit tags') : t('Add tags');
  };
  var renderErrorMessage = function renderErrorMessage() {
    return error ? /*#__PURE__*/React.createElement("div", {
      className: "cpc--resource-tag-modal-error cpc--resource-tag-modal-error__visible",
      id: "tag-modal-error-msg"
    }, error.message) : null;
  };
  var showAccessTags = accessTagPermissions.isCreateAccessTagEnabled || accessTagPermissions.isAttachAccessTagEnabled || accessTagPermissions.isDetachAccessTagEnabled;
  var renderPermissionsMessage = function renderPermissionsMessage() {
    var noEditPermissionAccessTags = !accessTagPermissions.isAttachAccessTagEnabled && !accessTagPermissions.isDetachAccessTagEnabled;
    var noEditPermissionUserTags = !userTagPermissions.isAttachUserTagEnabled && !userTagPermissions.isDetachUserTagEnabled;
    var permissionsMessage;
    if (showAccessTags && noEditPermissionAccessTags && noEditPermissionUserTags || !showAccessTags && noEditPermissionUserTags) {
      permissionsMessage = t('You do not have permission to edit tags. Contact your administrator if you require greater access.');
    } else if (showAccessTags && (!accessTagPermissions.isAttachAccessTagEnabled || !accessTagPermissions.isDetachAccessTagEnabled) || !userTagPermissions.isAttachUserTagEnabled || !userTagPermissions.isDetachUserTagEnabled) {
      permissionsMessage = t('You have restricted access and may not be able to add or remove tags. Contact your administrator if you require greater access.');
    }
    return permissionsMessage && /*#__PURE__*/React.createElement("div", {
      className: "cpc--resource-tag-modal-error cpc--resource-tag-modal-error__visible",
      id: "tag-modal-error-msg"
    }, /*#__PURE__*/React.createElement("strong", null, t('Restricted access')), " ", permissionsMessage);
  };
  var renderResourceName = function renderResourceName(hasAttachedTags) {
    if (isLoading || !resource) {
      return /*#__PURE__*/React.createElement(SkeletonText, {
        heading: true
      });
    }
    var subtitle = hasAttachedTags ? t("You're editing tags for:") : t("You're adding tags to:");
    return /*#__PURE__*/React.createElement("div", {
      className: "cpc--resource-tag-modal__subtitle"
    }, /*#__PURE__*/React.createElement("span", null, "".concat(subtitle, " ")), /*#__PURE__*/React.createElement("span", {
      className: "cpc--resource-tag-modal__resourcename"
    }, resource.name));
  };
  var tagModalClass = classnames('cpc--resource-tag-modal');
  var hasAttachedTags = resource && resource.tags && resource.tags.length > 0;
  var modalHeading = getModalHeading(hasAttachedTags);
  var primaryButtonText = isSaving ? t('Saving...') : t('Save');
  var attachedUserTags = resource && resource.tags ? resource.tags.map(function (tagName) {
    return {
      name: tagName
    };
  }) : [];
  var attachedAccessTags = resource && resource.access_tags ? resource.access_tags.map(function (tagName) {
    return {
      name: tagName
    };
  }) : [];
  var isAccessTagsEditable = accessTagPermissions.isAttachAccessTagEnabled;
  var resourceTagStrings = {
    exampleTag: t('costctr:124'),
    exampleOpeningLine: t('Use tags to organize your resources. If your user tags are billing related, consider writing tags as key:value pairs to help group related tags, such as'),
    tagVisibility: t('User tags are visible account-wide. Avoid including sensitive data in the tag name.'),
    placeholder: t('Examples: env:dev, version-1'),
    labelText: t('User tags'),
    labelTextOptional: t('User tags (optional')
  };
  var accessTagStrings = {
    exampleOpeningLine: t('Access management tags help you apply flexible access policies on specific resources.'),
    labelTextOptional: t('Access management tags (optional)'),
    placeholder: t('Examples: access:dev, proj:version-1'),
    labelText: t('Access management tags')
  };
  return /*#__PURE__*/React.createElement("div", {
    className: tagModalClass
  }, /*#__PURE__*/React.createElement(Modal, {
    id: "tag-modal",
    open: open,
    modalHeading: modalHeading
    // elements with role 'presentation' should not have an aria-label
    // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/presentation_role
    // modalAriaLabel={modalHeading}
    ,
    primaryButtonText: primaryButtonText,
    primaryButtonDisabled: validationError !== undefined || error !== undefined || isSaving || !modifiedTags.resourceTags.toAttach.length && !modifiedTags.resourceTags.toDetach.length && !modifiedTags.accessTags.toAttach.length && !modifiedTags.accessTags.toDetach.length,
    secondaryButtonText: t('Cancel'),
    onSecondarySubmit: handleOnClose,
    onRequestSubmit: handleOnSave,
    onRequestClose: handleOnClose
  }, renderResourceName(hasAttachedTags), renderErrorMessage(), !isLoading && renderPermissionsMessage(), /*#__PURE__*/React.createElement("div", {
    className: "cpc--tag-area-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: "cpc--resource-tags"
  }, isLoading ? /*#__PURE__*/React.createElement(TagAreaSkeleton, null) : /*#__PURE__*/React.createElement(TagAreaComponent, {
    tooltipAutoOrientation: true,
    isEditable: userTagPermissions.isAttachUserTagEnabled,
    autoFocus: true,
    inline: true,
    id: "input-resource-tags",
    toolTipDirection: tagAreaTooltipDirection,
    options: tags,
    light: false,
    tags: attachedUserTags,
    onChange: handleUserTagOnChange,
    onMessage: handleTagAreaMessage,
    labelColon: labelColon,
    allowAttach: userTagPermissions.isAttachUserTagEnabled,
    allowDetach: userTagPermissions.isDetachUserTagEnabled,
    tagAreaStrings: resourceTagStrings
  })), showAccessTags ? /*#__PURE__*/React.createElement("div", {
    className: "cpc--access-tags"
  }, isLoading ? /*#__PURE__*/React.createElement(TagAreaSkeleton, null) : /*#__PURE__*/React.createElement(TagAreaComponent, {
    tooltipAutoOrientation: true,
    isEditable: isAccessTagsEditable,
    autoFocus: false,
    inline: true,
    id: "input-iam-tags",
    toolTipDirection: tagAreaTooltipDirection,
    options: accessTags,
    light: false,
    tags: attachedAccessTags,
    tagPrefix: "".concat(t('IAM'), " | "),
    onChange: handleAccessTagOnChange,
    onMessage: handleTagAreaMessage,
    labelColon: labelColon,
    keyValueFormat: true,
    allowAttach: accessTagPermissions.isAttachAccessTagEnabled,
    allowDetach: accessTagPermissions.isDetachAccessTagEnabled,
    allowCreate: accessTagPermissions.isCreateAccessTagEnabled,
    tagAreaStrings: accessTagStrings
  })) : null)));
};
TagModal.propTypes = {
  /**
   * Indicates if the modal is open or not.
   */
  open: PropTypes.bool,
  /**
   * Function to call when modal is closed.
   */
  onClose: PropTypes.func.isRequired,
  /**
   * Function to call when the save button is clicked on the modal.
   */
  onSave: PropTypes.func.isRequired,
  /**
   * List of all available user tags.
   */
  tags: PropTypes.arrayOf(PropTypes.shape({})),
  /**
   * List of all available access tags.
   */
  accessTags: PropTypes.arrayOf(PropTypes.shape({})),
  /**
   * Permission state for actions associated with access tags
   */
  accessTagPermissions: PropTypes.shape({
    isCreateAccessTagEnabled: PropTypes.bool,
    isAttachAccessTagEnabled: PropTypes.bool,
    isDetachAccessTagEnabled: PropTypes.bool
  }),
  /**
   * If true, show UI in loading state.
   */
  isLoading: PropTypes.bool,
  /**
   * The resource to attach/detach tags to/from.
   */
  resource: PropTypes.shape({
    resourceId: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.node),
    access_tags: PropTypes.arrayOf(PropTypes.node)
  }),
  /**
   * Error message to show in the UI when required.
   */
  error: PropTypes.shape({
    message: PropTypes.string
  }),
  /**
   * Indicates whether a colon should be included in the tag area label.
   */
  labelColon: PropTypes.bool,
  /**
   * Whether or not we should use mock data to populate the modal.
   */
  mock: PropTypes.bool,
  /**
   * The location of the TagArea tooltip, relative to the trigger UI.
   */
  tagAreaTooltipDirection: PropTypes.oneOf(['bottom', 'top', 'left', 'right']),
  /**
   * Permission state for actions associated with user tags
   */
  userTagPermissions: PropTypes.shape({
    isAttachUserTagEnabled: PropTypes.bool,
    isDetachUserTagEnabled: PropTypes.bool
  })
};
TagModal.defaultProps = {
  open: false,
  isLoading: true,
  labelColon: true,
  mock: false,
  error: undefined,
  resource: undefined,
  accessTagPermissions: undefined,
  tagAreaTooltipDirection: undefined,
  tags: undefined,
  accessTags: undefined,
  userTagPermissions: undefined
};
var TagModalComponent = TagModal;

export { TagModalComponent as default };
